import type { FishIQSpeciesSeed } from '../types';

export type FishIQDistributionStatus = 'native' | 'established' | 'localized' | 'occasional' | 'unknown';

export type FishIQCanonicalMetadata = {
  canonicalId: string;
  legacyIds: string[];
  normalizedScientificName?: string;
  normalizedCommonName: string;
  aliases: string[];
  distributionStatus: FishIQDistributionStatus;
  habitatZones: string[];
  waterRealms: string[];
  dataQuality: {
    hasScientificName: boolean;
    hasWaterRealm: boolean;
    hasHabitat: boolean;
    hasRegion: boolean;
    hasSource: boolean;
    completeness: number;
  };
};

const clean = (value: unknown) => String(value ?? '').trim();
const lower = (value: unknown) => clean(value).toLowerCase();
const slugify = (value: unknown) => lower(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const isBroadScientificLabel = (value: unknown): boolean => {
  const raw = lower(value);
  if (!raw) return false;
  return /\bspp?\.?\b|\bspecies\b|\bcomplex\b|\//i.test(raw);
};

const unique = <T,>(items: T[], key = (value: T) => lower(value)): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const normalized = key(item);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(item);
  }
  return out;
};

const CANONICAL_ID_OVERRIDES: Record<string, string> = {
  barracuda_sea: 'great_barracuda',
  barracuda: 'great_barracuda',
  rabbitfish_sea: 'rabbitfish',
  yellowtail_amberjack: 'california_yellowtail',
  yellowtail: 'california_yellowtail',
  black_sea_bass_fish: 'black_sea_bass',
  muskellunge: 'muskie',
  peacock_bass_us: 'peacock_bass',
  apahap_barramundi: 'barramundi',
  yellowtail_amberjack_pacific: 'california_yellowtail',
  steelhead: 'steelhead',
  kokanee_salmon: 'kokanee_salmon',
  onaga: 'onaga',
  black_rockfish_asia: 'korean_rockfish',
  black_rockfish_japan: 'korean_rockfish',
  rohu_india: 'rohu',
  mrigal_india: 'mrigal_carp',
  striped_snakehead_india: 'striped_snakehead',
  walking_catfish_india: 'walking_catfish',
  tilapia_india: 'nile_tilapia',
  barramundi_india: 'barramundi',
  seer_fish_india: 'narrow_barred_spanish_mackerel',
  giant_trevally_india: 'giant_trevally',
  queenfish_india: 'queenfish',
  mangrove_jack_india: 'mangrove_jack',
};

const WATER_REALM_BY_CATEGORY: Record<string, string[]> = {
  freshwater: ['freshwater'],
  saltwater: ['coastal', 'offshore'],
  both: ['freshwater', 'brackish', 'coastal'],
};

const deriveHabitatZones = (species: Partial<FishIQSpeciesSeed>): string[] => {
  const source = [
    ...(Array.isArray(species.habitats) ? species.habitats : []),
    ...(Array.isArray(species.structureTags) ? species.structureTags : []),
  ].map(lower);

  const zones = new Set<string>();
  const addIf = (pattern: RegExp, zone: string) => {
    if (source.some((value) => pattern.test(value))) zones.add(zone);
  };

  addIf(/lake|reservoir|pond/, 'stillwater');
  addIf(/river|creek|stream|brook|current/, 'flowing-water');
  addIf(/bay|harbor|harbour|marina/, 'bay-harbor');
  addIf(/estuary|lagoon|brackish|mangrove|marsh|backwater/, 'estuary-wetland');
  addIf(/surf|beach/, 'surf');
  addIf(/reef|rock|jetty|breakwater|wreck/, 'hard-structure');
  addIf(/kelp|grass|weed|vegetation/, 'vegetation');
  addIf(/offshore|ocean|pelagic|bluewater/, 'offshore');
  addIf(/pier|dock/, 'manmade-structure');
  addIf(/deep|drop.?off|channel/, 'deep-edge');
  addIf(/flat|shoal|shallow/, 'shallow-flat');

  return [...zones];
};

export function canonicalSpeciesKey(species: Partial<FishIQSpeciesSeed>): string {
  const scientificRaw = clean(species.scientificName);
  const scientific = slugify(scientificRaw);

  // Exact scientific identity is the strongest dedupe key. This intentionally
  // runs before Roddo ID aliases so regional copies such as barramundi_india,
  // apahap_barramundi, and the base Barramundi collapse to one fish.
  if (scientific && !isBroadScientificLabel(scientificRaw)) {
    return `sci:${scientific}`;
  }

  // Broad taxonomy labels ("spp.", family groups, slash groups) are not exact
  // species identities. For those, explicit Roddo aliases remain useful.
  const id = slugify(species.id || species.slug);
  const overridden = CANONICAL_ID_OVERRIDES[id];
  if (overridden) return `id:${overridden}`;
  if (scientific) return `sci:${scientific}`;

  return `name:${slugify(species.commonName || species.id || species.slug)}`;
}

export function getFishIQPresentationIdentityKey(species: any): string {
  return canonicalSpeciesKey(species || {});
}

const LOCAL_GROUP_PROFILE_IDS = new Set([
  'philippines_lapu_lapu',
  'philippines_maya_maya',
  'philippines_talakitok',
  'grouper_sea',
  'snapper_sea',
]);

const queryMatchesSpecies = (species: any, query: string): boolean => {
  const q = lower(query);
  if (!q) return false;
  const values = [
    species?.id,
    species?.slug,
    species?.commonName,
    species?.name,
    species?.scientificName,
    ...(Array.isArray(species?.aliases) ? species.aliases : []),
  ].map(lower).filter(Boolean);
  return values.some((value) => value.includes(q) || q.includes(value));
};

const isLocalGroupProfile = (species: any): boolean => {
  const id = slugify(species?.id || species?.slug);
  if (LOCAL_GROUP_PROFILE_IDS.has(id)) return true;
  const scientific = clean(species?.scientificName);
  return isBroadScientificLabel(scientific) && /grouper|snapper|jack|trevally/i.test(
    `${species?.family || ''} ${species?.commonName || ''} ${species?.name || ''}`,
  );
};

const familyPresentationKey = (species: any): string =>
  slugify(species?.family || '');

const hasExactSpeciesIdentity = (species: any): boolean => {
  const scientific = clean(species?.scientificName);
  return Boolean(scientific && !isBroadScientificLabel(scientific));
};

export function dedupeFishIQSpeciesForPresentation<T = any>(
  items: T[] = [],
  options: { query?: string | null; suppressLocalGroups?: boolean } = {},
): T[] {
  const query = clean(options.query);
  const suppressLocalGroups = options.suppressLocalGroups !== false;
  const canonical: T[] = [];
  const seen = new Set<string>();

  // Keep the first copy because callers normally pass either relevance-ranked
  // or intentionally ordered lists. The canonical key removes exact regional
  // duplicates without changing that ordering.
  for (const item of Array.isArray(items) ? items : []) {
    if (!item) continue;
    const key = getFishIQPresentationIdentityKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    canonical.push(item);
  }

  if (!suppressLocalGroups) return canonical;

  return canonical.filter((item: any) => {
    if (!isLocalGroupProfile(item)) return true;

    // Group aliases are valuable when the angler explicitly searches the local
    // name. Otherwise, keep area/read lists species-level when a stronger exact
    // member of the same family is already present.
    if (queryMatchesSpecies(item, query)) return true;

    const family = familyPresentationKey(item);
    if (!family) return true;

    return !canonical.some((candidate: any) => {
      if (candidate === item) return false;
      return familyPresentationKey(candidate) === family && hasExactSpeciesIdentity(candidate);
    });
  });
}

function richerText(a: unknown, b: unknown) {
  return clean(b).length > clean(a).length ? b : a;
}

function mergeObject(a: any, b: any): any {
  if (!a) return b;
  if (!b) return a;
  return { ...a, ...b };
}

function mergeSpeciesRecord(a: FishIQSpeciesSeed, b: FishIQSpeciesSeed): FishIQSpeciesSeed {
  const merged: any = {
    ...a,
    ...b,
    id: a.id || b.id,
    slug: a.slug || b.slug,
    commonName: richerText(a.commonName, b.commonName),
    scientificName: richerText(a.scientificName, b.scientificName),
    summary: richerText(a.summary, b.summary),
    depthPreference: richerText(a.depthPreference, b.depthPreference),
    typicalSize: richerText(a.typicalSize, b.typicalSize),
    aliases: unique([...(a.aliases || []), ...(b.aliases || []), a.commonName, b.commonName].filter(Boolean) as string[]),
    habitats: unique([...(a.habitats || []), ...(b.habitats || [])] as any[]),
    regions: unique([...(a.regions || []), ...(b.regions || [])]),
    waterRealms: unique([...(a.waterRealms || []), ...(b.waterRealms || [])] as any[]),
    behaviorTags: unique([...(a.behaviorTags || []), ...(b.behaviorTags || [])]),
    structureTags: unique([...(a.structureTags || []), ...(b.structureTags || [])]),
    keyTraits: unique([...(a.keyTraits || []), ...(b.keyTraits || [])]),
    bestBaits: unique([...(a.bestBaits || []), ...(b.bestBaits || [])] as any[], (v: any) => lower(typeof v === 'string' ? v : v?.name || v?.label)),
    bestTechniques: unique([...(a.bestTechniques || []), ...(b.bestTechniques || [])] as any[], (v: any) => lower(typeof v === 'string' ? v : v?.name || v?.label)),
    bestSeasons: unique([...(a.bestSeasons || []), ...(b.bestSeasons || [])] as any[]),
    bestTimesOfDay: unique([...(a.bestTimesOfDay || []), ...(b.bestTimesOfDay || [])] as any[]),
    beginnerTips: unique([...(a.beginnerTips || []), ...(b.beginnerTips || [])]),
    advancedTips: unique([...(a.advancedTips || []), ...(b.advancedTips || [])]),
    publicSourceRefs: unique([...(a.publicSourceRefs || []), ...(b.publicSourceRefs || [])]),
    editorialSources: unique([...(a.editorialSources || []), ...(b.editorialSources || [])] as any[], (v: any) => `${lower(v?.title)}|${lower(v?.uri)}`),
    feedingProfile: mergeObject(a.feedingProfile, b.feedingProfile),
    speciesKnowledge: mergeObject(a.speciesKnowledge, b.speciesKnowledge),
  };

  const legacyIds = unique([
    ...((a as any).canonicalSpecies?.legacyIds || []),
    ...((b as any).canonicalSpecies?.legacyIds || []),
    a.id,
    b.id,
  ].filter(Boolean));

  merged.canonicalSpecies = buildFishIQCanonicalMetadata(merged, legacyIds);
  return merged as FishIQSpeciesSeed;
}

export function buildFishIQCanonicalMetadata(
  species: Partial<FishIQSpeciesSeed>,
  legacyIds: string[] = [clean(species.id)].filter(Boolean),
): FishIQCanonicalMetadata {
  const rawId = slugify(species.id || species.slug || species.commonName);
  const scientificRaw = clean(species.scientificName);
  const scientificKey = slugify(scientificRaw);
  const canonicalId =
    scientificKey && !isBroadScientificLabel(scientificRaw)
      ? scientificKey
      : CANONICAL_ID_OVERRIDES[rawId] || scientificKey || rawId;
  const category = lower(species.category);
  const waterRealms = unique([
    ...((species.waterRealms || []) as string[]),
    ...(WATER_REALM_BY_CATEGORY[category] || []),
  ]);
  const habitatZones = deriveHabitatZones(species);
  const aliases = unique([...(species.aliases || []), clean(species.commonName)].filter(Boolean));
  const hasSource = Boolean((species.publicSourceRefs || []).length || (species.editorialSources || []).length);
  const checks = [
    Boolean(clean(species.scientificName)),
    waterRealms.length > 0,
    Boolean((species.habitats || []).length),
    Boolean((species.regions || []).length),
    hasSource,
  ];

  return {
    canonicalId,
    legacyIds: unique(legacyIds),
    normalizedScientificName: scientificKey || undefined,
    normalizedCommonName: slugify(species.commonName),
    aliases,
    distributionStatus: ((species as any).distributionStatus || 'unknown') as FishIQDistributionStatus,
    habitatZones,
    waterRealms,
    dataQuality: {
      hasScientificName: checks[0],
      hasWaterRealm: checks[1],
      hasHabitat: checks[2],
      hasRegion: checks[3],
      hasSource: checks[4],
      completeness: Math.round((checks.filter(Boolean).length / checks.length) * 100),
    },
  };
}

export function mergeCanonicalFishIQSpeciesSeeds(...groups: FishIQSpeciesSeed[][]): FishIQSpeciesSeed[] {
  const byKey = new Map<string, FishIQSpeciesSeed>();

  for (const group of groups) {
    for (const raw of group || []) {
      if (!raw?.id && !raw?.commonName) continue;
      const item: any = { ...raw };
      item.canonicalSpecies = buildFishIQCanonicalMetadata(item);
      const key = canonicalSpeciesKey(item);
      const existing = byKey.get(key);
      byKey.set(key, existing ? mergeSpeciesRecord(existing, item) : item);
    }
  }

  return [...byKey.values()].sort((a, b) => clean(a.commonName).localeCompare(clean(b.commonName)));
}

export function getCanonicalFishIQMetadata(species: any): FishIQCanonicalMetadata {
  return species?.canonicalSpecies || buildFishIQCanonicalMetadata(species || {});
}
